import { DirectPaths } from "@/config/routes";
import {
    SHARE_MARKET_TODAY_PAGES,
    getIndexPageUrl,
    getIndicesListUrl,
    getShareMarketTodayListUrl,
    getShareMarketTodayPageUrl,
    getStockPageUrl,
} from "@/app/utils/stock-url";

/**
 * Static navigation links for SEO / ICM 3.0 header.
 * Top-level items open a Navigation Menu dropdown.
 */
export type NavIconKey =
    | "stocks"
    | "global-stocks"
    | "mutual-funds"
    | "ipos"
    | "etfs"
    | "futures-options"
    | "digital-gold"
    | "digital-silver"
    | "fixed-deposits"
    | "bonds"
    | "calendar-days"
    | "calculator"
    | "chart-candlestick"
    | "chart-area"
    | "chart-spline"
    | "lightbulb"
    | "clapperboard"
    | "book-open-text"
    | "shuffle"
    | "indian-rupee"
    | "chart-no-axes-column"
    | "fingerprint"
    | "portfolio";

export interface StaticNavSubLink {
    label: string;
    href: string;
    /**
     * When set, used instead of `href` for authenticated users
     * (e.g. FD → ICM_FD_POST_URL + /alts/fd).
     */
    authenticatedHref?: string;
    icon?: NavIconKey;
    featured?: boolean;
    /** When true, link opens in a new browser tab. */
    openInNewTab?: boolean;
    eventName?: string;
    branchEvent?: string;
}

export interface StaticNavSection {
    title?: string;
    items: StaticNavSubLink[];
}

export interface MarketsPanelColumnItem {
    label: string;
    href: string;
    featured?: boolean;
    /** When true, link opens in a new browser tab. */
    openInNewTab?: boolean;
}

export interface MarketsCategory {
    label: string;
    icon?: NavIconKey;
    /** When set, clicking the sidebar item navigates here. Hover can still reveal `panel`. */
    href?: string;
    /** When true, the category href opens in a new browser tab. */
    openInNewTab?: boolean;
    panel?: {
        title: string;
        /** Hub page for the panel title (SEO internal link). Falls back to category `href`. */
        href?: string;
        columns: MarketsPanelColumnItem[][];
    };
}

export type NavLayout = "grid" | "sidebar";

export interface StaticNavLink {
    label: string;
    icon?: NavIconKey;
    layout?: NavLayout;
    sections?: StaticNavSection[];
    sidebarTitle?: string;
    categories?: MarketsCategory[];
    /** When set with no sections/categories, this is a standalone top-level link. */
    href?: string;
    eventName?: string;
    branchEvent?: string;
    openInNewTab?: boolean;
    /** When true, only shown after login (and not on `/`), matching legacy header. */
    authOnly?: boolean;
}

export interface AuthLink {
    label: string;
    href: string;
    eventName?: string;
    openInNewTab?: boolean;
}

const MARKET_INDICES = [
    "Nifty 50",
    "Nifty Bank",
    "BSE 100 ESG",
    "Nifty 500",
    "BSE Sensex",
    "Nifty IT",
    "Nifty 100",
    "Nifty FMCG",
    "Nifty Oil & Gas",
    "Nifty Metal",
    "Nifty Pharma",
    "Nifty Cement",
] as const;

const POPULAR_STOCKS = [
    "Tata Power Company Ltd",
    "State Bank of India",
    "Reliance Industries Ltd",
    "Adani Power Ltd",
    "ITC Ltd",
    "Infosys Ltd",
    "Suzlon Energy Ltd",
    "Tata Consultancy Services Ltd",
    "Wipro Ltd",
    "Yes Bank Ltd",
    "Jio Financial Services Ltd",
    "Hindustan Aeronautics Ltd",
] as const;

function toIndexNavItem(name: string): MarketsPanelColumnItem {
    return { label: name, href: getIndexPageUrl(name) };
}

function toShareMarketTodayNavItem(name: string): MarketsPanelColumnItem {
    return { label: name, href: getShareMarketTodayPageUrl(name) };
}

function toStockNavItem(name: string): MarketsPanelColumnItem {
    return { label: name, href: getStockPageUrl(name) };
}

function chunkNavItems(
    items: MarketsPanelColumnItem[],
    columnCount: number,
): MarketsPanelColumnItem[][] {
    const size = Math.ceil(items.length / columnCount);
    return Array.from({ length: columnCount }, (_, index) =>
        items.slice(index * size, index * size + size),
    );
}

export const staticNavLinks: StaticNavLink[] = [
    {
        label: "Products",
        icon: "chart-no-axes-column",
        layout: "grid",
        sections: [
            {
                title: "PRODUCTS",
                items: [
                    {
                        label: "Stocks",
                        href: DirectPaths.STOCKS,
                        icon: "stocks",
                        eventName: "Home_Nav_Bar_Broking",
                        branchEvent: "Navigation_Broking",
                    },
                    {
                        label: "Global Stocks",
                        href: DirectPaths.GLOBAL + DirectPaths.US_STOCKS,
                        icon: "global-stocks",
                        eventName: "Home_Nav_Bar_US_Stocks",
                        branchEvent: "Navigation_Shares_US_Stocks",
                    },
                    {
                        label: "Global ETFs",
                        href: DirectPaths.GLOBAL + DirectPaths.US_ETFS,
                        icon: "global-stocks",
                        eventName: "Home_Nav_Bar_US_ETFs",
                        branchEvent: "Navigation_Shares_US_ETFs",
                    },
                    // {
                    //     label: "Mutual Funds",
                    //     href: DirectPaths.STOCKS,
                    //     icon: "mutual-funds",
                    // },
                    { label: "IPOs", href: `${process.env.ICM_PROD_URL}${DirectPaths.IPO}`, icon: "ipos" },
                    {
                        label: "ETFs",
                        href: `${process.env.ICM_PROD_URL}${DirectPaths.ETF}`,
                        icon: "etfs",
                        eventName: "Home_Nav_Bar_US_ETF",
                        branchEvent: "Navigation_Shares_US_ETF",
                    },
                    // {
                    //     label: "Futures and Options",
                    //     href: DirectPaths.STOCKS,
                    //     icon: "futures-options",
                    // },
                ],
            },
            {
                title: "ALTS",
                items: [
                    {
                        label: "Digital Gold",
                        href: `${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.GOLD}`,
                        icon: "digital-gold",
                        eventName: "Home_Explore_on_Digital_Gold&_Silver",
                        branchEvent: "Navigation_Bullion",
                    },
                    {
                        label: "Digital Silver",
                        href: `${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.SILVER}`,
                        icon: "digital-silver",
                        eventName: "Home_Explore_on_Digital_Gold&_Silver",
                        branchEvent: "Navigation_Bullion",
                    },
                    {
                        label: "Fixed Deposits",
                        href: DirectPaths.FD_POSTFIX_PATH,
                        // Post-login: alts host from next.config (`ICM_FD_POST_URL`) + /alts/fd
                        authenticatedHref: `${process.env.ICM_FD_POST_URL ?? ""}${DirectPaths.FD_POSTFIX_PATH}`,
                        icon: "fixed-deposits",
                        eventName: "Home_Explore_Fixed_Deposit",
                        branchEvent: "Navigation_FD",
                    },
                    {
                        label: "Bonds",
                        href: process.env.ICM_BONDS_URL ?? "/",
                        icon: "bonds",
                        eventName: "Home_Explore_All_Bonds",
                        branchEvent: "Navigation_Alts",
                    },
                ],
            },
        ],
    },
    {
        label: "Markets",
        icon: "chart-spline",
        layout: "sidebar",
        sidebarTitle: "MARKETS",
        categories: [
            {
                label: "Indices",
                icon: "calendar-days",
                href: getIndicesListUrl(),
                panel: {
                    title: "INDICES",
                    columns: chunkNavItems(MARKET_INDICES.map(toIndexNavItem), 2),
                },
            },
            {
                label: "Share Market Today",
                icon: "calendar-days",
                href: getShareMarketTodayListUrl(),
                panel: {
                    title: "SHARE MARKET TODAY",
                    columns: chunkNavItems(
                        SHARE_MARKET_TODAY_PAGES.map(toShareMarketTodayNavItem),
                        2,
                    ),
                },
            },
            {
                label: "Popular Stocks",
                icon: "calendar-days",
                panel: {
                    title: "POPULAR STOCKS",
                    columns: chunkNavItems(POPULAR_STOCKS.map(toStockNavItem), 2),
                },
            },
        ],
    },
    // {
    //     label: "Platform & Tools",
    //     icon: "chart-no-axes-column",
    //     layout: "grid",
    //     sections: [
    //         {
    //             items: [
    //                 {
    //                     label: "InCred Money Broking App",
    //                     href: DirectPaths.BROKING,
    //                     icon: "stocks",
    //                 },
    //                 {
    //                     label: "Fair Value Calculator",
    //                     href: DirectPaths.CALCULATOR,
    //                     icon: "calculator",
    //                 },
    //                 {
    //                     label: "API",
    //                     href: DirectPaths.BROKING,
    //                     icon: "chart-candlestick",
    //                 },
    //                 {
    //                     label: "Epic Fury",
    //                     href: DirectPaths.BROKING,
    //                     icon: "chart-area",
    //                 },
    //                 {
    //                     label: "Trading Terminal",
    //                     href: DirectPaths.BROKING,
    //                     icon: "chart-spline",
    //                 },
    //             ],
    //         },
    //     ],
    // },
    {
        label: "Portfolio",
        icon: "portfolio",
        href: `${process.env.ICM_URL ?? ""}/portfolio`,
        branchEvent: "Navigation_Portfolio",
        authOnly: true,
    },
    {
        label: "Learn",
        icon: "chart-candlestick",
        layout: "grid",
        sections: [
            {
                items: [
                    {
                        label: "Knowledge Center",
                        href: process.env.ICM_KNOWLEDGE_CENTRE ?? "/",
                        icon: "lightbulb",
                        openInNewTab: true,
                        eventName: "Home_Nav_Bar_Knowledge_Center",
                        branchEvent: "Navigation_Knowledge_Center",
                    },
                    {
                        label: "Media",
                        href: process.env.ICM_MEDIA ?? "/",
                        icon: "clapperboard",
                        openInNewTab: true,
                        eventName: "Home_Nav_Bar_Media",
                        branchEvent: "Navigation_Media",
                    },
                    {
                        label: "Blogs",
                        href: process.env.ICM_LEARN ?? "/",
                        icon: "book-open-text",
                        openInNewTab: true,
                        eventName: "Home_Nav_Bar_Blogs",
                        branchEvent: "Navigation_Blogs",
                    },
                    // {
                    //     label: "Infographics",
                    //     href: "/learn",
                    //     icon: "chart-area",
                    // },
                    // {
                    //     label: "Seminar/YT",
                    //     href: "/learn",
                    //     icon: "chart-spline",
                    // },
                    // {
                    //     label: "News",
                    //     href: "/media",
                    //     icon: "bonds",
                    // },
                ],
            },
        ],
    },
    {
        label: "About",
        icon: "fingerprint",
        href: "/about",
        eventName: "Home_Nav_Bar_About_Us",
    },
    // {
    //     label: "More",
    //     icon: "digital-gold",
    //     layout: "grid",
    //     sections: [
    //         {
    //             items: [
    //                 {
    //                     label: "Refer",
    //                     href: "/refer",
    //                     icon: "shuffle",
    //                 },
    //                 {
    //                     label: "Pricing",
    //                     href: DirectPaths.BROKING,
    //                     icon: "indian-rupee",
    //                 },
    //             ],
    //         },
    //     ],
    // },
];

/** Auth CTAs — shared by SEO header + NavigationAuthButtons. */
export const authLinks: readonly AuthLink[] = [
    { label: "Sign in", href: "/login", eventName: "Top_Nav_Bar_Login" },
    {
        label: "Open Demat Account",
        href: `${process.env.ICM_PROD_URL}/open-demat-account`,
        openInNewTab: true,
    },
] as const;

/** Flat label+href list for SEO crawlers (sr-only header). */
export function getSeoNavLinks(): { label: string; href: string }[] {
    const links: { label: string; href: string }[] = [{ label: "Home", href: "/" }];

    for (const item of staticNavLinks) {
        if (item.authOnly) continue;
        if (item.href) {
            links.push({ label: item.label, href: item.href });
        }
        for (const section of item.sections ?? []) {
            for (const sub of section.items) {
                if (sub.href) {
                    links.push({ label: sub.label, href: sub.href });
                }
            }
        }
        for (const category of item.categories ?? []) {
            if (category.href) {
                links.push({ label: category.label, href: category.href });
            }
            for (const column of category.panel?.columns ?? []) {
                for (const cell of column) {
                    if (cell.href) {
                        links.push({ label: cell.label, href: cell.href });
                    }
                }
            }
        }
    }

    return links;
}
